/* global BigInt */
import { encodeFunctionData } from "viem";
import { getCrashGameContract, getGameLiquidityContract } from "../../utils/contractHelpers";
import {
  CRASH_GAME_CONTRACT,
  LIQUIDITY_CONTRACT,
  usdr_token,
} from "../../utils/Enviroment";
import useWeb3 from "../useWeb3";
import crashGameAbi from "../../utils/CrashGameAbi.json";
import liquidityAddressAbi from "../../utils/GameLiquidity.json";
import { userSmartAccountClient } from "../../components/RecoilAtom";
import { useRecoilValue } from "recoil";
import BalanceOfAbi from "../../utils/BalanceOfAbi.json";
import {
  handleGasFeeWithPreparedCalls,
  sendGasFeeAsUsdt,
  convertToMwei,
  convertFromWei,
} from "../../utils/helpers";
import { signPreparedCalls } from "@account-kit/wallet-client";
import { USDR_VAULT_SHARE_DECIMALS } from "../../utils/constants";
const useCrashDataFetcher = () => {
  const web3 = useWeb3();
  const smartAccountClient = useRecoilValue(userSmartAccountClient);

  const getTotalVolumn = async (helper, addresss) => {
    try {
      const contract = helper(addresss, web3);
      const balance = await contract?.methods?.totalRewards()?.call();
      const formattedBalance = convertFromWei(balance, "ether");
      return formattedBalance;
    } catch (error) {
      console.error("Error fetching totalVolume:", error.message || error);
      return "0";
    }
  };
  const getMaxTrade = async (helper, addresss) => {
    try {
      const contract = helper(addresss, web3);
      const balance = await contract?.methods?.totalAssets(usdr_token)?.call();
      const formattedBalance = convertFromWei(balance, "ether");
      return formattedBalance;
    } catch (error) {
      console.error("Error fetching totalLiquidity:", error.message || error);
      return "0";
    }
  };
  const getGameRoundByIndex = async (index) => {
    try {
      const contract = await getCrashGameContract(CRASH_GAME_CONTRACT, web3);
      const allowance = await contract.methods.marketTermination(index).call();
      return allowance;
    } catch (error) {
      console.error("Check Allowance error:", error);
      throw error;
    }
  };

  const getCrashPlatformFee = async () => {
    try {
      const contract = await getGameLiquidityContract(LIQUIDITY_CONTRACT, web3);
      const platformFee = await contract.methods.totalFees(usdr_token).call();
      const formattedPlatformFee = web3.utils.fromWei(String(platformFee), "ether");
      return formattedPlatformFee;
    } catch (error) {
      console.error("Check PlatformFee error:", error);
      throw error;
    }
  };
  const AddLiqudity = async (
    amount,
    approveAmount,
    smartAccount,
    permissions,
    sessionkey,
    smartAccountUsdtBalance
  ) => {
    const convertedAmount = convertToMwei(amount, "ether");
    const largeAmountApproval = 10000000000000000000000000000000000000000n;

    const callArgs = [];

    if (approveAmount) {
      callArgs.push({
        to: usdr_token,
        data: encodeFunctionData({
          abi: BalanceOfAbi,
          functionName: "approve",
          args: [LIQUIDITY_CONTRACT, largeAmountApproval],
        }),
      });
    }

    callArgs.push({
      to: LIQUIDITY_CONTRACT,
      data: encodeFunctionData({
        abi: liquidityAddressAbi,
        functionName: "deposit",
        args: [usdr_token, convertedAmount, smartAccount],
      }),
    });

    const gasFee = await handleGasFeeWithPreparedCalls(
      smartAccountClient,
      smartAccount,
      callArgs
    );
    const maxDeposit = smartAccountUsdtBalance - gasFee;

    if (amount > maxDeposit) {
      throw new Error(
        `Insufficient funds to cover gas fee .
        `
      );
    }

    callArgs.push({
      to: usdr_token,
      data: sendGasFeeAsUsdt(gasFee),
    });

    try {
      const preparedCalls = await smartAccountClient.prepareCalls({
        calls: callArgs,
        from: smartAccount,
        capabilities: { permissions },
      });

      const signedCalls = await signPreparedCalls(sessionkey, preparedCalls);
      const { preparedCallIds } = await smartAccountClient.sendPreparedCalls({
        ...signedCalls,
        capabilities: { permissions },
      });
      const status = await smartAccountClient.getCallsStatus(preparedCallIds[0]);
      return status;
    } catch (e) {
      console.error("Error in AddLiqudity:", e);
      throw e;
    }
  };

  const WithDrawHook = async (
    amount,
    isApprovalNeeded,
    smartAccount,
    permissions,
    sessionkey
  ) => {
    const originalAmount = BigInt(convertToMwei(amount, USDR_VAULT_SHARE_DECIMALS));
    const callArgs = [];
    if (isApprovalNeeded) {
      callArgs.push({
        to: usdr_token,
        data: encodeFunctionData({
          abi: BalanceOfAbi,
          functionName: "approve",
          args: [LIQUIDITY_CONTRACT, 10000000000000000000000000000000000000000n],
        }),
      });
    }
    const redeemCallForGas = [
      {
        to: LIQUIDITY_CONTRACT,
        data: encodeFunctionData({
          abi: liquidityAddressAbi,
          functionName: "redeem",
          args: [usdr_token, originalAmount, smartAccount],
        }),
      },
    ];

    let gasFee = await handleGasFeeWithPreparedCalls(smartAccountClient, smartAccount, redeemCallForGas);
    const gasFeeWithBuffer = gasFee * 1.1;
    const gasFeeInMwei = BigInt(convertToMwei(gasFeeWithBuffer.toString(), USDR_VAULT_SHARE_DECIMALS));
    const adjustedRedeemAmount = originalAmount - gasFeeInMwei;

    if (adjustedRedeemAmount <= 0n) {
      throw new Error("Insufficient amount to cover gas fees");
    }

    callArgs.push({
      to: LIQUIDITY_CONTRACT,
      data: encodeFunctionData({
        abi: liquidityAddressAbi,
        functionName: "redeem",
        args: [usdr_token, adjustedRedeemAmount, smartAccount],
      }),
    });
    callArgs.push({
      to: usdr_token,
      data: sendGasFeeAsUsdt(gasFeeWithBuffer),
    });

    try {
      const preparedCalls = await smartAccountClient.prepareCalls({
        calls: callArgs,
        from: smartAccount,
        capabilities: { permissions },
      });

      const signedCalls = await signPreparedCalls(sessionkey, preparedCalls);

      const { preparedCallIds } = await smartAccountClient.sendPreparedCalls({
        ...signedCalls,
        capabilities: { permissions },
      });

      const status = await smartAccountClient.getCallsStatus(preparedCallIds[0]);
      return status;
    } catch (e) {
      throw e;
    }
  };


  const Unreserve = async (smartAccount, permissions, sessionKey) => {
    const callArgs = [
      {
        to: LIQUIDITY_CONTRACT,
        data: encodeFunctionData({
          abi: liquidityAddressAbi,
          functionName: "unreserve",
          args: [usdr_token],
        }),
      },
    ];

    const gasFee = await handleGasFeeWithPreparedCalls(smartAccountClient, smartAccount, callArgs);

    callArgs.push({
      to: usdr_token,
      data: sendGasFeeAsUsdt(gasFee),
    });

    try {
      const preparedCalls = await smartAccountClient.prepareCalls({
        calls: callArgs,
        from: smartAccount,
        capabilities: { permissions },
      });

      const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);

      const { preparedCallIds } = await smartAccountClient.sendPreparedCalls({
        ...signedCalls,
        capabilities: { permissions },
      });

      const status = await smartAccountClient.getCallsStatus(preparedCallIds[0]);
      return status;
    } catch (e) {
      throw e;
    }
  };

  const userClaimRewards = async (smartAccount, permissions, sessionKey) => {
    const callArgs = [
      {
        to: CRASH_GAME_CONTRACT,
        data: encodeFunctionData({
          abi: crashGameAbi,
          functionName: "claimRewards",
          args: [],
        }),
      },
    ];

    const gasFee = await handleGasFeeWithPreparedCalls(smartAccountClient, smartAccount, callArgs);
    callArgs.push({
      to: usdr_token,
      data: sendGasFeeAsUsdt(gasFee),
    });

    try {
      const preparedCalls = await smartAccountClient.prepareCalls({
        calls: callArgs,
        from: smartAccount,
        capabilities: { permissions },
      });

      const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);

      const { preparedCallIds } = await smartAccountClient.sendPreparedCalls({
        ...signedCalls,
        capabilities: { permissions },
      });

      const status = await smartAccountClient.getCallsStatus(preparedCallIds[0]);
      return status;
    } catch (e) {
      throw e;
    }
  };


  return {
    getTotalVolumn,
    getMaxTrade,
    getGameRoundByIndex,
    getCrashPlatformFee,
    AddLiqudity,
    WithDrawHook,
    userClaimRewards,
    Unreserve

  };
};

export default useCrashDataFetcher;